/**
 * A realtime duel's board (ADR 0077): both players play at once, each on their own machine.
 *
 * The core waits for the server's start (both players there, plus a three-second
 * count-in), mounts the board, and relays frames both ways over the duel's topic. A game
 * that ends says `over` to the opponent and sends its log; hearing the opponent's `over`
 * stops this game and sends its log too. A `beat` every two seconds tells the server this
 * player is still there, and ten seconds without a word from the opponent asks the server,
 * which decides whether they left. The server replays both logs and gives the result.
 */
import {
  Suspense,
  type ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import type { DuelFrame, DuelLog, RealtimeBoardProps, RealtimeHandle } from "@/duels/registry";
import { useRealtimeSocket } from "@/core/realtime/RealtimeProvider";
import { duelTopic, opponentOf, postLog, type DuelDetail } from "./api";

const BEAT_MS = 2000;
const SILENCE_MS = 11_000;

export function RealtimeArea({
  duel,
  Board,
  frameListeners,
  socketOpen,
}: {
  duel: DuelDetail;
  Board: ComponentType<RealtimeBoardProps>;
  frameListeners: Set<(frame: DuelFrame) => void>;
  socketOpen: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const socket = useRealtimeSocket();
  const handleRef = useRef<RealtimeHandle | null>(null);
  const boardListeners = useRef(new Set<(frame: DuelFrame) => void>());
  const sent = useRef(duel.my_side !== null && duel.logs_sent.includes(duel.my_side));
  const lastHeard = useRef(Date.now());
  const [sendFailed, setSendFailed] = useState<DuelLog | null>(null);
  const topic = duelTopic(duel.id);
  const queryKey = ["duels", "duel", duel.id];

  // The server's start, on this machine's clock. The offset is measured once per reading:
  // measured at every render, it would slide the start along with the count-in.
  const offset = useMemo(() => Date.parse(duel.server_now) - Date.now(), [duel.server_now]);
  const startsAt = duel.turn_started_at ? Date.parse(duel.turn_started_at) - offset : null;
  const [now, setNow] = useState(Date.now());
  const counting = duel.status === "active" && startsAt !== null && now < startsAt;
  useEffect(() => {
    if (!counting) return;
    const id = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(id);
  }, [counting]);
  const started = duel.status === "active" && startsAt !== null && now >= startsAt;
  const playing = started && !sent.current;

  const submit = useCallback(
    (log: DuelLog) => {
      if (sent.current) return;
      sent.current = true;
      postLog(duel.id, log)
        .then((next) => {
          setSendFailed(null);
          queryClient.setQueryData(["duels", "duel", duel.id], next);
        })
        .catch(() => {
          sent.current = false;
          setSendFailed(log);
        });
    },
    [duel.id, queryClient],
  );

  // Frames from the opponent: `over` ends this game too; the rest go to the board.
  useEffect(() => {
    const listen = (frame: DuelFrame) => {
      if (frame.side === duel.my_side) return;
      lastHeard.current = Date.now();
      if (frame.kind === "over") {
        const log = handleRef.current?.end();
        if (log) submit(log);
        return;
      }
      boardListeners.current.forEach((handler) => handler(frame));
    };
    frameListeners.add(listen);
    return () => {
      frameListeners.delete(listen);
    };
  }, [frameListeners, duel.my_side, submit]);

  // The heartbeat, and the watchdog on the opponent's silence.
  useEffect(() => {
    if (!playing) return;
    lastHeard.current = Date.now();
    const id = window.setInterval(() => {
      socket?.send({ type: "game", topic, kind: "beat", data: null });
      if (Date.now() - lastHeard.current > SILENCE_MS) {
        lastHeard.current = Date.now();
        void queryClient.invalidateQueries({ queryKey });
      }
    }, BEAT_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, socket, topic, queryClient]);

  // The server ended the duel (a disconnection, the cap): stop the board where it is.
  useEffect(() => {
    if (duel.status === "finished" || duel.status === "aborted") handleRef.current?.end();
  }, [duel.status]);

  const send = useCallback(
    (kind: string, data: unknown) => socket?.send({ type: "game", topic, kind, data }),
    [socket, topic],
  );
  const onFrame = useCallback((handler: (frame: DuelFrame) => void) => {
    boardListeners.current.add(handler);
    return () => {
      boardListeners.current.delete(handler);
    };
  }, []);
  const onOver = useCallback(
    (log: DuelLog) => {
      socket?.send({ type: "game", topic, kind: "over", data: null });
      submit(log);
    },
    [socket, topic, submit],
  );

  if (!socketOpen && duel.status !== "finished")
    return (
      <p className="bt-duel__notice" role="alert">
        {t("duels.realtime_offline")}
      </p>
    );
  if (counting && startsAt !== null)
    return (
      <p className="bt-duel__count-in" role="timer" aria-live="assertive">
        {Math.ceil((startsAt - now) / 1000)}
      </p>
    );
  if (!started || duel.seed === undefined || startsAt === null) return null;
  return (
    <>
      <Suspense fallback={<p className="bt-page-message">{t("app.loading")}</p>}>
        <Board
          seed={duel.seed}
          side={duel.my_side!}
          startsAt={startsAt}
          opponentName={opponentOf(duel)?.name ?? ""}
          send={send}
          onFrame={onFrame}
          handleRef={handleRef}
          onOver={onOver}
        />
      </Suspense>
      {sendFailed ? (
        <div className="bt-duel__notice" role="alert">
          <p>{t("duels.log_failed")}</p>
          <button
            type="button"
            className="bt-button bt-button--secondary bt-button--sm"
            onClick={() => submit(sendFailed)}
          >
            {t("games.retry")}
          </button>
        </div>
      ) : null}
      {sent.current && duel.status === "active" ? (
        <p className="bt-duel__status" role="status">
          {t("duels.waiting_result")}
        </p>
      ) : null}
    </>
  );
}
